import React, { FC } from 'react';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useTiltakEnergiPlan } from '../../../../hooks/useTiltakEnergiplan';
import { Soknad } from '../../../../types/building';
import { TintedCard } from '../styledComponents';
import { useEnergiplanRegistrations } from '../../hooks/useEnergiplanRegistrations';

import { Actions, ContentCard, TiltakList, TiltakSectionCard } from './components/styledComponents';
import { TiltakItemCard } from './components/tiltakItemCard';
import { CreateTiltakRegistrationButton } from './components/createTiltakRegistrationButton';
import { ArchiveRegistrationButton } from './components/archiveRegistrationButton';
import { TiltakLoader } from './components/tiltakLoader';

interface Props {
  soknad?: Soknad;
}

/**
 * Renders tiltak content based on Energiplan progression:
 * 1. Step 1 not published: show informational state until publishing is complete.
 * 2. Missing step 2 registration: show empty state with action to create it.
 * 3. Step 2 exists but no tiltak: show empty state with action to add first tiltak.
 * 4. Default: show tiltak list and context-specific actions for publish/archive.
 */
export const TiltakCard: FC<Props> = ({ soknad }) => {
  const { t } = useTranslation('', { keyPrefix: 'screens.energiplan.components.tiltak' });
  const { energyAnalysisSteg1, energyAnalysisSteg2, energyAnalysisSteg3 } = useEnergiplanRegistrations(soknad);
  const { tiltakList, isLoading } = useTiltakEnergiPlan(energyAnalysisSteg2?.energiplan?.energiplanId);

  const isDone = !!energyAnalysisSteg2?.energiplan?.energimerke?.properties?.lukketSoknadSteg2;
  const hasTiltak = tiltakList && tiltakList?.length > 0;
  const steg1isPublished = !!energyAnalysisSteg1?.registrering.publisertDato;
  const canArchiveSteg2 = !energyAnalysisSteg3?.registrering;
  const nyttTiltakPath = `nytt-tiltak/${energyAnalysisSteg2?.energiplan?.energiplanId}/`;

  if (isLoading) {
    return <TiltakLoader />;
  }

  if (!steg1isPublished) {
    return (
      <ContentCard>
        <Heading data-size="md">{t('heading')}</Heading>
        <Paragraph data-size="xs">{t('estimateText')}</Paragraph>
      </ContentCard>
    );
  }

  if (!energyAnalysisSteg2) {
    return (
      <TintedCard>
        <Heading level={3} data-size="sm">
          {t('heading')}
        </Heading>
        <Paragraph>{t('noTiltak')}</Paragraph>
        <Actions>
          <CreateTiltakRegistrationButton registrering={energyAnalysisSteg1?.registrering} />
        </Actions>
      </TintedCard>
    );
  }

  if (steg1isPublished && !hasTiltak) {
    return (
      <ContentCard>
        <Heading level={3} data-size="sm">
          {t('heading')}
        </Heading>
        <Paragraph>{t('noTiltak')}</Paragraph>
        <Actions>
          <Button asChild>
            <Link to={nyttTiltakPath}>{t('addButton')}</Link>
          </Button>
        </Actions>
      </ContentCard>
    );
  }

  return (
    <TiltakSectionCard>
      <Heading level={3} data-size="sm">
        {t('heading')}
      </Heading>
      <TiltakList>
        {tiltakList?.map((tiltak) => (
          <TiltakItemCard key={tiltak.tiltakId} tiltak={tiltak} isDone={isDone} />
        ))}
      </TiltakList>

      <Actions>
        {!isDone && (
          <>
            <Button variant="secondary" asChild>
              <Link to={nyttTiltakPath}>{t('newButton')}</Link>
            </Button>

            <Button variant="secondary" asChild>
              <Link to={`../registrer-energimerke/${energyAnalysisSteg2.registreringId}/om-boligen`}>
                {t('finishButton')}
              </Link>
            </Button>
          </>
        )}
        {isDone && canArchiveSteg2 && (
          <ArchiveRegistrationButton
            registrationId={energyAnalysisSteg2?.registreringId}
            energiplanId={energyAnalysisSteg2?.energiplan?.energiplanId}
          />
        )}
      </Actions>
    </TiltakSectionCard>
  );
};
